import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import api from '../services/api';
import { HeaderIOS, IOS } from './shared/MenuLayout';
import {
  Chart as ChartJS, ArcElement, CategoryScale, LinearScale,
  BarElement, Tooltip, Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Doughnut, Bar } from 'react-chartjs-2';
import { usePermisos } from '../hooks/usePermisos';
import ReactDOM from 'react-dom';

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

// ── helpers ───────────────────────────────────────────────────────────────────
const fmt  = n => `S/ ${parseFloat(n||0).toLocaleString('es-PE',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const fmtN = n => parseFloat(n||0).toLocaleString('es-PE',{minimumFractionDigits:2,maximumFractionDigits:2});
const COLORS = ['#007AFF','#34C759','#FF9500','#FF3B30','#5E5CE6','#5AC8FA','#FF2D55','#30D158'];

const MOTIVOS_CIERRE = [
  'CIERRE TURNO MAÑANA',
  'CIERRE TURNO TARDE',
  'CIERRE TURNO NOCHE',
  'CIERRE EXTEMPORÁNEO',
  'CIERRE POR ERROR DE APERTURA',
  'CIERRE DIFERIDO',
  'CIERRE EXTRAORDINARIO',
  'CIERRE ADMINISTRATIVO',
];

const TH = ({children,right,center,w}) => (
  <th style={{
    padding:'9px 10px', width:w,
    textAlign:center?'center':right?'right':'left',
    fontSize:'0.6rem', fontWeight:700, color:IOS.label2,
    letterSpacing:'0.06em', textTransform:'uppercase',
    backgroundColor:IOS.surface, borderBottom:`1px solid ${IOS.separator}`,
    whiteSpace:'nowrap', position:'sticky', top:0, zIndex:1
  }}>{children}</th>
);

// ── KPI Card ──────────────────────────────────────────────────────────────────
const KpiCard = ({ label, valor, sub, color, icon, grande }) => (
  <Box sx={{bgcolor:IOS.surface,borderRadius:'16px',p:2,border:`1px solid ${IOS.separator}`,boxShadow:'0 1px 4px rgba(0,0,0,0.07)'}}>
    <Box sx={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:1}}>
      <Box sx={{minWidth:0,flex:1}}>
        <Typography sx={{fontSize:'0.6rem',fontWeight:700,color:IOS.label2,letterSpacing:'0.07em',textTransform:'uppercase',mb:0.5,lineHeight:1.3}}>{label}</Typography>
        <Typography sx={{fontWeight:800,fontSize:grande?{xs:'1.3rem',sm:'1.6rem'}:'1rem',color,fontVariantNumeric:'tabular-nums',lineHeight:1,wordBreak:'break-all'}}>{fmt(valor)}</Typography>
        {sub&&<Typography sx={{fontSize:'0.62rem',color:IOS.label2,mt:0.4,lineHeight:1.3}}>{sub}</Typography>}
      </Box>
      <Box sx={{width:34,height:34,borderRadius:'10px',bgcolor:`${color}12`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>{icon}</Box>
    </Box>
  </Box>
);

const Sel = ({ value, onChange, children, w, disabled }) => {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const ref = React.useRef(null);
  const opciones = React.Children.map(children, ch => ({
    value: String(ch.props.value ?? ''), label: ch.props.children,
  })) || [];
  const selLabel = opciones.find(o => o.value === String(value || ''))?.label || '';
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const handleOpen = () => {
    if (disabled) return;
    if (ref.current) setRect(ref.current.getBoundingClientRect());
    setOpen(v => !v);
  };
  return (
    <Box ref={ref} sx={{ position:'relative', width:w||'100%' }}>
      <Box onClick={handleOpen}
        sx={{ height:40, px:1.5, pr:3.5, display:'flex', alignItems:'center',
          border:`1.5px solid ${open?IOS.blue:IOS.blue}`,borderRadius:10,
          bgcolor:disabled?IOS.bg:IOS.surface, cursor:disabled?'default':'pointer',
          userSelect:'none', position:'relative', opacity:disabled?0.5:1,
          boxShadow:open?`0 0 0 3px ${IOS.blue}18`:'none', transition:'all 0.15s' }}>
        <Typography sx={{ fontSize:'0.85rem', color:IOS.label, flex:1,
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{selLabel}</Typography>
        <Box sx={{ position:'absolute', right:10, top:'50%',
          transform:`translateY(-50%) rotate(${open?180:0}deg)`, transition:'transform 0.2s' }}>
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
            <path d="M1 1l4 4 4-4" stroke={IOS.label2} strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </Box>
      </Box>
      {open && rect && ReactDOM.createPortal(
        <Box sx={{ position:'fixed', top:rect.bottom+4, left:rect.left, zIndex:99999,
          bgcolor:IOS.surface, border:`1px solid ${IOS.separator}`, borderRadius:'12px',
          boxShadow:'0 8px 24px rgba(0,0,0,0.13)', minWidth:rect.width, maxHeight:240,
          overflowY:'auto', '&::-webkit-scrollbar':{width:3},
          '&::-webkit-scrollbar-thumb':{bgcolor:IOS.separator,borderRadius:4} }}>
          {opciones.map(op => (
            <Box key={op.value}
              onMouseDown={e => { e.preventDefault(); onChange({target:{value:op.value}}); setOpen(false); }}
              sx={{ px:1.5, py:0.9, cursor:'pointer', whiteSpace:'nowrap',
                bgcolor:String(value||'')===op.value?`${IOS.blue}08`:'transparent',
                '&:hover':{ bgcolor:IOS.bg } }}>
              <Typography sx={{ fontSize:'0.85rem',
                color:String(value||'')===op.value?IOS.blue:IOS.label,
                fontWeight:String(value||'')===op.value?700:400 }}>{op.label}</Typography>
            </Box>
          ))}
        </Box>,
        document.body
      )}
    </Box>
  );
};

// ── Modal confirmar cierre ────────────────────────────────────────────────────
const ModalCierre = ({ open, kpi, apertura, onClose, onConfirm }) => {
  const [monto,        setMonto]        = useState('');
  const [motivoCombo,  setMotivoCombo]  = useState('');
  const [textoLibre,   setTextoLibre]   = useState('');
  const [cerrando,     setCerrando]     = useState(false);
  const [msgErr,       setMsgErr]       = useState('');

  useEffect(() => {
    if (open) {
      setMonto(''); setMotivoCombo(''); setTextoLibre('');
      setMsgErr(''); setCerrando(false);
    }
  }, [open]);

  const diferencia = useMemo(() => {
    const m = parseFloat(monto.replace(',','.') || '0');
    return m - (kpi?.saldoFinal || 0);
  }, [monto, kpi]);

  const observacionFinal = textoLibre.trim() || motivoCombo || null;

  const handleConfirm = async () => {
    const m = parseFloat(monto.replace(',','.') || '0');
    if (!monto || isNaN(m) || m < 0) { setMsgErr('Ingresa un monto válido'); return; }
    setCerrando(true); setMsgErr('');
    try {
      const r = await api.post('/farmacia/corte/cerrar', {
        idApertura:  apertura?.id,
        montoReal:   m,
        observacion: observacionFinal,
      });
      if (r.data.success) { onConfirm(r.data); }
      else setMsgErr(r.data.message || 'Error al cerrar');
    } catch(e) { setMsgErr(e?.response?.data?.message || 'Error al cerrar'); }
    finally { setCerrando(false); }
  };

  if (!open) return null;
  return (
    <Box sx={{
      position:'fixed',inset:0,bgcolor:'rgba(0,0,0,0.55)',zIndex:9999,
      display:'flex',alignItems:{xs:'flex-end',sm:'center'},justifyContent:'center',
      backdropFilter:'blur(6px)',px:{xs:0,sm:2}
    }}>
      <Box sx={{
        bgcolor:IOS.surface,
        borderRadius:{xs:'20px 20px 0 0',sm:'20px'},
        width:'100%',maxWidth:460,
        boxShadow:'0 24px 60px rgba(0,0,0,0.3)',
        overflow:'hidden',
        maxHeight:{xs:'95vh',sm:'90vh'},
        display:'flex',flexDirection:'column'
      }}>
        {/* Header rojo */}
        <Box sx={{bgcolor:IOS.red,px:2.5,pt:2.5,pb:2,flexShrink:0}}>
          <Box sx={{display:'flex',alignItems:'center',gap:1.5,mb:1.5}}>
            <Box sx={{width:40,height:40,borderRadius:'50%',bgcolor:'rgba(255,255,255,0.2)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><line x1="12" y1="11" x2="12" y2="15"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            </Box>
            <Box sx={{minWidth:0}}>
              <Typography sx={{color:'white',fontWeight:700,fontSize:'0.95rem'}}>Confirmar cierre de caja</Typography>
              <Typography sx={{color:'rgba(255,255,255,0.75)',fontSize:'0.72rem',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{apertura?.descripcion}</Typography>
            </Box>
          </Box>
          <Box sx={{bgcolor:'rgba(255,255,255,0.15)',borderRadius:'12px',p:1.5,textAlign:'center'}}>
            <Typography sx={{color:'rgba(255,255,255,0.75)',fontSize:'0.65rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.07em',mb:0.3}}>Saldo esperado en caja</Typography>
            <Typography sx={{color:'white',fontWeight:800,fontSize:{xs:'1.6rem',sm:'2rem'},fontVariantNumeric:'tabular-nums',lineHeight:1}}>{fmt(kpi?.saldoFinal)}</Typography>
            <Typography sx={{color:'rgba(255,255,255,0.6)',fontSize:'0.65rem',mt:0.3}}>Base + Contado - NC</Typography>
          </Box>
        </Box>

        {/* Cuerpo scrollable */}
        <Box sx={{px:2.5,py:2,display:'flex',flexDirection:'column',gap:1.5,overflowY:'auto',
          '&::-webkit-scrollbar':{width:3},'&::-webkit-scrollbar-thumb':{bgcolor:IOS.separator,borderRadius:4}}}>

          <Box>
            <Typography sx={{fontSize:'0.6rem',fontWeight:700,color:IOS.label2,textTransform:'uppercase',letterSpacing:'0.07em',mb:0.5}}>Monto contado en caja *</Typography>
            <Box sx={{position:'relative'}}>
              <Typography sx={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',fontSize:'0.9rem',fontWeight:700,color:IOS.label2,pointerEvents:'none'}}>S/</Typography>
              <input
                type="number" step="0.01" min="0"
                value={monto}
                onChange={e=>setMonto(e.target.value)}
                placeholder="0.00"
                style={{
                  width:'100%',height:48,paddingLeft:32,paddingRight:12,
                  border:`2px solid ${monto?IOS.blue:IOS.separator}`,
                  borderRadius:12,fontSize:'1.2rem',fontWeight:700,color:IOS.label,
                  outline:'none',backgroundColor:IOS.surface,fontFamily:'monospace',
                  boxSizing:'border-box'
                }}
                onFocus={e=>e.target.style.borderColor=IOS.blue}
                onBlur={e=>e.target.style.borderColor=monto?IOS.blue:IOS.separator}
              />
            </Box>
            {monto&&(
              <Box sx={{mt:0.8,display:'flex',alignItems:'center',gap:0.6,px:1.2,py:0.6,borderRadius:'9px',
                bgcolor:Math.abs(diferencia)<0.01?`${IOS.green}10`:diferencia>0?`${IOS.blue}10`:`${IOS.red}10`,
                border:`1px solid ${Math.abs(diferencia)<0.01?IOS.green:diferencia>0?IOS.blue:IOS.red}30`}}>
                <Box sx={{width:5,height:5,borderRadius:'50%',bgcolor:Math.abs(diferencia)<0.01?IOS.green:diferencia>0?IOS.blue:IOS.red,flexShrink:0}}/>
                <Typography sx={{fontSize:'0.7rem',fontWeight:700,color:Math.abs(diferencia)<0.01?IOS.green:diferencia>0?IOS.blue:IOS.red}}>
                  {Math.abs(diferencia)<0.01
                    ? 'Cuadra perfectamente ✓'
                    : diferencia>0
                      ? `Sobrante: S/ ${fmtN(diferencia)}`
                      : `Faltante: S/ ${fmtN(Math.abs(diferencia))}`
                  }
                </Typography>
              </Box>
            )}
          </Box>

          <Box>
            <Typography sx={{fontSize:'0.6rem',fontWeight:700,color:IOS.label2,textTransform:'uppercase',letterSpacing:'0.07em',mb:0.5}}>Observación (opcional)</Typography>
            <select
              value={motivoCombo}
              onChange={e=>{ setMotivoCombo(e.target.value); setTextoLibre(''); }}
              style={{
                width:'100%',height:40,padding:'0 10px',
                border:`1.5px solid ${IOS.separator}`,borderRadius:10,
                fontSize:'0.85rem',color:motivoCombo?IOS.label:IOS.label2,
                outline:'none',backgroundColor:IOS.surface,fontFamily:'inherit',
                boxSizing:'border-box',marginBottom:6,cursor:'pointer',appearance:'auto'
              }}
              onFocus={e=>e.target.style.borderColor=IOS.blue}
              onBlur={e=>e.target.style.borderColor=IOS.separator}>
              <option value="">— Seleccionar motivo predefinido —</option>
              {MOTIVOS_CIERRE.map(o=>(
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
            <Box sx={{display:'flex',alignItems:'center',gap:1,my:0.5}}>
              <Box sx={{flex:1,height:'1px',bgcolor:IOS.separator}}/>
              <Typography sx={{fontSize:'0.58rem',color:IOS.label2,fontWeight:600}}>O ESCRIBE LIBREMENTE</Typography>
              <Box sx={{flex:1,height:'1px',bgcolor:IOS.separator}}/>
            </Box>
            <textarea
              value={textoLibre}
              onChange={e=>{ setTextoLibre(e.target.value); if(e.target.value) setMotivoCombo(''); }}
              placeholder="Observación personalizada..."
              rows={2}
              style={{
                width:'100%',padding:'10px 12px',
                border:`1.5px solid ${textoLibre?IOS.blue:IOS.separator}`,
                borderRadius:10,fontSize:'0.85rem',color:IOS.label,
                outline:'none',backgroundColor:IOS.surface,fontFamily:'inherit',
                boxSizing:'border-box',resize:'none'
              }}
              onFocus={e=>e.target.style.borderColor=IOS.blue}
              onBlur={e=>e.target.style.borderColor=textoLibre?IOS.blue:IOS.separator}
            />
            {observacionFinal&&(
              <Box sx={{mt:0.5,px:1.2,py:0.6,borderRadius:'8px',bgcolor:`${IOS.blue}08`,border:`1px solid ${IOS.blue}20`}}>
                <Typography sx={{fontSize:'0.65rem',color:IOS.blue,fontWeight:600}}>
                  Se registrará: "{observacionFinal}"
                </Typography>
              </Box>
            )}
          </Box>

          {msgErr&&(
            <Box sx={{p:1.2,borderRadius:'10px',bgcolor:'#FFF0EF',border:'1px solid #FFD4D1'}}>
              <Typography sx={{fontSize:'0.78rem',fontWeight:600,color:IOS.red}}>{msgErr}</Typography>
            </Box>
          )}

          <Box sx={{p:1.2,bgcolor:'#FFF8E6',borderRadius:'10px',border:'1px solid #FFE9A0',display:'flex',gap:0.8,alignItems:'flex-start'}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8B6914" strokeWidth="2" strokeLinecap="round" style={{flexShrink:0,marginTop:1}}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            <Typography sx={{fontSize:'0.7rem',color:'#8B6914',lineHeight:1.5}}>Esta acción cerrará la apertura y no podrá revertirse. Verifica el monto antes de confirmar.</Typography>
          </Box>

          <Box sx={{display:'flex',gap:1,mt:0.5,pb:{xs:1,sm:0}}}>
            <Box component="button" onClick={onClose} disabled={cerrando}
              sx={{flex:1,height:44,borderRadius:'12px',border:`1px solid ${IOS.separator}`,bgcolor:IOS.surface,cursor:'pointer'}}>
              <Typography sx={{fontSize:'0.85rem',fontWeight:600,color:IOS.label2}}>Cancelar</Typography>
            </Box>
            <Box component="button" onClick={handleConfirm} disabled={cerrando||!monto}
              sx={{flex:2,height:44,borderRadius:'12px',border:'none',
                bgcolor:cerrando||!monto?'#E5E5EA':IOS.red,
                cursor:cerrando||!monto?'default':'pointer',
                display:'flex',alignItems:'center',justifyContent:'center',gap:1}}>
              {cerrando&&<CircularProgress size={15} sx={{color:'white'}}/>}
              <Typography sx={{fontSize:'0.9rem',fontWeight:800,color:cerrando||!monto?'#8E8E93':'white'}}>
                {cerrando?'Cerrando...':'Confirmar cierre'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
const CorteCajaDatafarma = () => {
  const navigate     = useNavigate();
  const user         = authService.getCurrentUser();
  const ueId         = user?.ueIdEstablecimiento||user?.UeIdEstablecimiento||0;
  const nf           = user?.nombreCompleto||user?.usuario||'';
  const handleLogout = ()=>{ authService.logout(); navigate('/login'); };

  const { privilegioDeOpcion } = usePermisos();
  const privCorte = privilegioDeOpcion('cortecaja', 3);
  const esTOTAL   = privCorte === 'Total';

  const [aperturas,     setAperturas]     = useState([]);
  const [aperturaSel,   setAperturaSel]   = useState(null);
  const [kpi,           setKpi]           = useState(null);
  const [formaPago,     setFormaPago]     = useState([]);
  const [cpe,           setCpe]           = useState([]);
  const [cargando,      setCargando]      = useState(false);
  const [cargAperturas, setCargAperturas] = useState(true);
  const [modalCierre,   setModalCierre]   = useState(false);
  const [msgOk,         setMsgOk]         = useState('');
  const [msgErr,        setMsgErr]        = useState('');
  const [yaCerrado,     setYaCerrado]     = useState(false);
  const [modalPrint,    setModalPrint]    = useState(false);
  const [pdfUrl,        setPdfUrl]        = useState(null);
  const [cargDet,       setCargDet]       = useState(false);
  const [formatoActivo, setFormatoActivo] = useState('A4');

  // ── Cargar aperturas ───────────────────────────────────────────────────────
  useEffect(() => {
    api.get('/farmacia/corte/aperturas', { params: { ueId } })
      .then(r => {
        if (r.data.success) {
          const lista = r.data.data || [];
          setAperturas(lista);
          const nombreUsuario = (nf||'').toLowerCase();
          const listaFiltrada = esTOTAL
          ? lista
          : lista.filter(a => {
              const palabras = (user?.nombreCompleto || '').toUpperCase().split(' ').filter(Boolean);
              const desc = a.descripcion.toUpperCase();
              return palabras.every(p => desc.includes(p));
            });
          setAperturas(listaFiltrada);
          const palabras = (user?.nombreCompleto || '').toUpperCase().split(' ').filter(Boolean);
          const match = listaFiltrada.find(a => 
            palabras.every(p => a.descripcion.toUpperCase().includes(p))
          ) || listaFiltrada[0];
          if (match) setAperturaSel(match);
        }
      })
      .catch(console.error)
      .finally(()=>setCargAperturas(false));
  }, [ueId, nf]);

  // ── Cargar datos ───────────────────────────────────────────────────────────
  const cargarDatos = useCallback(async (id) => {
    if (!id) return;
    setCargando(true); setKpi(null); setFormaPago([]); setCpe([]);
    try {
      const [rK,rF,rC] = await Promise.all([
        api.get('/farmacia/corte/kpi',        {params:{idApertura:id}}),
        api.get('/farmacia/corte/forma-pago', {params:{idApertura:id}}),
        api.get('/farmacia/corte/cpe',        {params:{idApertura:id}}),
      ]);
      if (rK.data.success) setKpi(rK.data.data);
      if (rF.data.success) setFormaPago(rF.data.data||[]);
      if (rC.data.success) setCpe(rC.data.data||[]);
    } catch(e){ console.error(e); }
    finally { setCargando(false); }
  }, []);

  useEffect(()=>{ if(aperturaSel?.id) cargarDatos(aperturaSel.id); },[aperturaSel,cargarDatos]);

  useEffect(()=>{ setYaCerrado(false); setPdfUrl(null); },[aperturaSel?.id]);

  const abrirPdf = useCallback(async (idAp, formato='A4') => {
    if (!idAp) return;
    setFormatoActivo(formato);
    setCargDet(true); setPdfUrl(null); setModalPrint(true);
    try {
      const r = await api.get('/farmacia/corte/reporte-pdf', {
        params: { idApertura: idAp, formato },
        responseType: 'blob',
      });
      setPdfUrl(URL.createObjectURL(new Blob([r.data],{type:'application/pdf'})));
    } catch(e) {
      console.error(e);
      setModalPrint(false);
    } finally {
      setCargDet(false);
    }
  }, []);

  const handleCierreConfirmado = useCallback((data) => {
    setModalCierre(false);
    setYaCerrado(true);
    setMsgOk(`Cierre generado correctamente (ID: ${data.idCierre})`);
    api.get('/farmacia/corte/aperturas',{params:{ueId}})
      .then(r=>{ if(r.data.success) setAperturas(r.data.data||[]); });
    abrirPdf(aperturaSel?.id, 'A4');
  }, [ueId, aperturaSel, abrirPdf]);

  // ── Gráficos ───────────────────────────────────────────────────────────────
  const chartFP = useMemo(()=>({
    labels: formaPago.map(f=>f.nombre),
    datasets:[{data:formaPago.map(f=>parseFloat(f.total||0)),backgroundColor:COLORS,borderWidth:0,hoverOffset:6}]
  }),[formaPago]);

  const chartCpe = useMemo(()=>{
    const activos = cpe.filter(c=>c.estado?.toUpperCase()==='ACTIVO');
    return {
      labels: activos.map(c=>c.tipoCpe),
      datasets:[{data:activos.map(c=>Math.abs(parseFloat(c.total||0))),backgroundColor:COLORS,borderRadius:8,borderWidth:0}]
    };
  },[cpe]);

  const baseOpts = {responsive:true,maintainAspectRatio:false};

  return (
    <>
      <ModalCierre
        open={modalCierre}
        kpi={kpi}
        apertura={aperturaSel}
        onClose={()=>setModalCierre(false)}
        onConfirm={handleCierreConfirmado}
      />

      <Box sx={{minHeight:'100vh',bgcolor:IOS.bg}}>
        <HeaderIOS titulo="Corte de Caja" subtitulo="Datafarma"
          onHome={()=>navigate('/datafarma')} onLogout={handleLogout}
          nombreCompleto={nf} avatarLetra={nf.charAt(0).toUpperCase()}/>

        <Box sx={{maxWidth:1400,mx:'auto',px:{xs:1.5,sm:2.5},pt:2,pb:8}}>
          {msgOk&&(
            <Box sx={{mb:2,p:1.5,borderRadius:'12px',bgcolor:'#EDFAF2',border:'1px solid #B7EFD0',display:'flex',alignItems:'center',justifyContent:'space-between',gap:1}}>
              <Typography sx={{fontSize:'0.82rem',fontWeight:600,color:'#1A6B3C',flex:1}}>{msgOk}</Typography>
              <Box component="button" onClick={()=>setMsgOk('')} sx={{border:'none',bgcolor:'transparent',cursor:'pointer',color:'#1A6B3C',fontSize:'1.1rem',lineHeight:1,flexShrink:0}}>×</Box>
            </Box>
          )}
          {msgErr&&(
            <Box sx={{mb:2,p:1.5,borderRadius:'12px',bgcolor:'#FFF0EF',border:'1px solid #FFD4D1'}}>
              <Typography sx={{fontSize:'0.82rem',fontWeight:600,color:'#C0392B'}}>{msgErr}</Typography>
            </Box>
          )}

          <Box sx={{bgcolor:IOS.surface,borderRadius:'16px',p:2,mb:2,border:`1px solid ${IOS.separator}`,boxShadow:'0 1px 4px rgba(0,0,0,0.07)'}}>
            <Box sx={{display:'flex',gap:1.5,alignItems:'flex-end',flexWrap:'wrap'}}>
              <Box sx={{flex:1,minWidth:{xs:'100%',sm:200}}}>
                <Typography sx={{fontSize:'0.6rem',fontWeight:700,color:IOS.label2,textTransform:'uppercase',letterSpacing:'0.07em',mb:0.5}}>Apertura de caja</Typography>
                {cargAperturas
                  ?<Box sx={{height:40,display:'flex',alignItems:'center'}}><CircularProgress size={16} sx={{color:IOS.blue}}/></Box>
                  :<Sel
                    value={String(aperturaSel?.id||'')}
                    disabled={aperturas.length <= 1}
                    onChange={e=>{
                      const ap=aperturas.find(a=>String(a.id)===e.target.value);
                      setAperturaSel(ap||null);
                    }}>
                    {aperturas.map(a=>(
                      <option key={a.id} value={a.id}>{a.descripcion}</option>
                    ))}
                  </Sel>
                }
              </Box>
              <Box sx={{display:'flex',gap:1,flexWrap:'wrap',alignItems:'center'}}>
                <Box component="button"
                  onClick={()=>aperturaSel&&cargarDatos(aperturaSel.id)}
                  disabled={cargando||!aperturaSel}
                  sx={{height:40,px:2,borderRadius:'10px',border:`1px solid ${IOS.separator}`,bgcolor:IOS.bg,cursor:'pointer',display:'flex',alignItems:'center',gap:0.6}}>
                  {cargando
                    ?<CircularProgress size={13} sx={{color:IOS.blue}}/>
                    :<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2.2" strokeLinecap="round"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
                  }
                  <Typography sx={{fontSize:'0.78rem',fontWeight:600,color:IOS.blue}}>Actualizar</Typography>
                </Box>
                {aperturaSel&&kpi&&(
                  yaCerrado
                    ? <Box component="button"
                        onClick={()=>abrirPdf(aperturaSel.id,'A4')}
                        sx={{height:40,px:2.5,borderRadius:'10px',border:'none',bgcolor:'#FF9500',cursor:'pointer',display:'flex',alignItems:'center',gap:0.8,boxShadow:'0 4px 12px rgba(255,149,0,0.35)'}}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                        <Typography sx={{fontSize:'0.82rem',fontWeight:700,color:'white'}}>Imprimir corte</Typography>
                      </Box>
                    : <Box component="button"
                        onClick={()=>setModalCierre(true)}
                        sx={{height:40,px:2.5,borderRadius:'10px',border:'none',bgcolor:IOS.red,cursor:'pointer',display:'flex',alignItems:'center',gap:0.8,boxShadow:`0 4px 12px ${IOS.red}30`}}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
                        <Typography sx={{fontSize:'0.82rem',fontWeight:700,color:'white'}}>Generar cierre</Typography>
                      </Box>
                )}
              </Box>
            </Box>
          </Box>

          {cargando&&(
            <Box sx={{py:10,textAlign:'center'}}><CircularProgress sx={{color:IOS.blue}}/></Box>
          )}

          {!cargando&&kpi&&(
            <>
              <Box sx={{display:'grid',gridTemplateColumns:{xs:'1fr 1fr',sm:'repeat(3,1fr)',md:'repeat(6,1fr)'},gap:1.5,mb:2}}>
                <KpiCard label="Base inicial"   valor={kpi.baseInicialCaja}         color={IOS.blue}   icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={IOS.blue}   strokeWidth="1.8" strokeLinecap="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>}/>
                <KpiCard label="Ventas contado" valor={kpi.ventasContado}            color={IOS.green}  icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={IOS.green}  strokeWidth="1.8" strokeLinecap="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>} sub={`# ${kpi.comprobantesContado} comprobantes`}/>
                <KpiCard label="Ventas crédito" valor={kpi.ventasCredito}            color={IOS.orange} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={IOS.orange} strokeWidth="1.8" strokeLinecap="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>}/>
                <KpiCard label="Notas crédito"  valor={kpi.ncTotal}                  color={IOS.red}    icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={IOS.red}    strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>} sub={`# ${kpi.comprobantesNC} NC`}/>
                <KpiCard label="Anulaciones"    valor={kpi.anulacionesSimplesMonto}  color={IOS.purple} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={IOS.purple} strokeWidth="1.8" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/></svg>} sub={`# ${kpi.anulacionesSimplesCnt} anulaciones`}/>
                <KpiCard label="Saldo final"    valor={kpi.saldoFinal}               color={IOS.green}  grande icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={IOS.green}  strokeWidth="1.8" strokeLinecap="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>}/>
              </Box>

              <Box sx={{display:'grid',gridTemplateColumns:{xs:'1fr',lg:'1fr 1fr'},gap:2,mb:2}}>
                <Box sx={{bgcolor:IOS.surface,borderRadius:'16px',border:`1px solid ${IOS.separator}`,boxShadow:'0 1px 4px rgba(0,0,0,0.07)',overflow:'hidden'}}>
                  <Box sx={{px:2.5,py:1.5,borderBottom:`1px solid ${IOS.separator}`,display:'flex',alignItems:'center',gap:1}}>
                    <Box sx={{width:28,height:28,borderRadius:'8px',bgcolor:`${IOS.blue}12`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                    </Box>
                    <Typography sx={{fontWeight:700,fontSize:'0.88rem',color:IOS.label}}>Detalle por forma de pago</Typography>
                  </Box>
                  <Box sx={{overflowX:'auto'}}>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.78rem',minWidth:320}}>
                      <thead>
                        <tr>
                          <TH>Tipo</TH><TH>Descripción</TH><TH center>Cant.</TH><TH right>Total</TH>
                        </tr>
                      </thead>
                      <tbody>
                        {formaPago.length===0
                          ? <tr><td colSpan={4} style={{padding:'24px',textAlign:'center',color:IOS.label2}}>Sin datos</td></tr>
                          : formaPago.map((f,i)=>(
                            <tr key={i} style={{borderBottom:`1px solid ${IOS.separator}`,backgroundColor:IOS.surface}}
                              onMouseEnter={e=>e.currentTarget.style.backgroundColor=IOS.bg}
                              onMouseLeave={e=>e.currentTarget.style.backgroundColor=IOS.surface}>
                              <td style={{padding:'9px 10px'}}>
                                <Box sx={{display:'inline-flex',px:0.8,py:0.2,borderRadius:'6px',bgcolor:f.tipoPago==='EFECTIVO'?`${IOS.green}12`:`${IOS.orange}12`}}>
                                  <Typography sx={{fontSize:'0.62rem',fontWeight:700,color:f.tipoPago==='EFECTIVO'?IOS.green:IOS.orange}}>{f.tipoPago}</Typography>
                                </Box>
                              </td>
                              <td style={{padding:'9px 10px',color:IOS.label}}>{f.nombre}</td>
                              <td style={{padding:'9px 10px',textAlign:'center',color:IOS.label2}}>{f.cantidad}</td>
                              <td style={{padding:'9px 10px',textAlign:'right',fontWeight:700,color:IOS.green,whiteSpace:'nowrap'}}>S/{fmtN(f.total)}</td>
                            </tr>
                          ))
                        }
                        {formaPago.length>0&&(
                          <tr style={{backgroundColor:IOS.bg,borderTop:`2px solid ${IOS.separator}`}}>
                            <td colSpan={2} style={{padding:'9px 10px',fontWeight:700,fontSize:'0.75rem',color:IOS.label}}>TOTAL</td>
                            <td style={{padding:'9px 10px',textAlign:'center',fontWeight:700,color:IOS.label}}>{formaPago.reduce((s,f)=>s+f.cantidad,0)}</td>
                            <td style={{padding:'9px 10px',textAlign:'right',fontWeight:800,color:IOS.green,fontSize:'0.88rem',whiteSpace:'nowrap'}}>S/{fmtN(formaPago.reduce((s,f)=>s+parseFloat(f.total||0),0))}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </Box>
                </Box>

                <Box sx={{bgcolor:IOS.surface,borderRadius:'16px',border:`1px solid ${IOS.separator}`,boxShadow:'0 1px 4px rgba(0,0,0,0.07)',overflow:'hidden'}}>
                  <Box sx={{px:2.5,py:1.5,borderBottom:`1px solid ${IOS.separator}`,display:'flex',alignItems:'center',gap:1}}>
                    <Box sx={{width:28,height:28,borderRadius:'8px',bgcolor:`${IOS.orange}12`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={IOS.orange} strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    </Box>
                    <Typography sx={{fontWeight:700,fontSize:'0.88rem',color:IOS.label}}>Detalle por tipo CPE</Typography>
                  </Box>
                  <Box sx={{overflowX:'auto'}}>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.78rem',minWidth:320}}>
                      <thead>
                        <tr>
                          <TH>Tipo CPE</TH><TH center>Estado</TH><TH center>Cant.</TH><TH right>Total</TH>
                        </tr>
                      </thead>
                      <tbody>
                        {cpe.length===0
                          ? <tr><td colSpan={4} style={{padding:'24px',textAlign:'center',color:IOS.label2}}>Sin datos</td></tr>
                          : cpe.map((c,i)=>{
                              const esActivo=c.estado?.toUpperCase()==='ACTIVO';
                              return(
                                <tr key={i} style={{borderBottom:`1px solid ${IOS.separator}`,backgroundColor:IOS.surface}}
                                  onMouseEnter={e=>e.currentTarget.style.backgroundColor=IOS.bg}
                                  onMouseLeave={e=>e.currentTarget.style.backgroundColor=IOS.surface}>
                                  <td style={{padding:'9px 10px',fontWeight:600,color:IOS.label}}>{c.tipoCpe}</td>
                                  <td style={{padding:'9px 10px',textAlign:'center'}}>
                                    <Box sx={{display:'inline-flex',alignItems:'center',gap:0.4,px:0.7,py:0.2,borderRadius:'20px',bgcolor:esActivo?'#EDFAF2':'#FFF0EF',border:`1px solid ${esActivo?'#B7EFD0':'#FFD4D1'}`}}>
                                      <Box sx={{width:4,height:4,borderRadius:'50%',bgcolor:esActivo?IOS.green:IOS.red}}/>
                                      <Typography sx={{fontSize:'0.58rem',fontWeight:700,color:esActivo?'#1A6B3C':'#C0392B'}}>{c.estado}</Typography>
                                    </Box>
                                  </td>
                                  <td style={{padding:'9px 10px',textAlign:'center',color:IOS.label2}}>{c.cantidad}</td>
                                  <td style={{padding:'9px 10px',textAlign:'right',fontWeight:700,color:parseFloat(c.total||0)<0?IOS.red:IOS.green,whiteSpace:'nowrap'}}>S/{fmtN(c.total)}</td>
                                </tr>
                              );
                            })
                        }
                      </tbody>
                    </table>
                  </Box>
                </Box>
              </Box>

              <Box sx={{display:'grid',gridTemplateColumns:{xs:'1fr',md:'1fr 1fr'},gap:2}}>
                <Box sx={{bgcolor:IOS.surface,borderRadius:'16px',p:2.5,border:`1px solid ${IOS.separator}`,boxShadow:'0 1px 4px rgba(0,0,0,0.07)'}}>
                  <Typography sx={{fontWeight:700,fontSize:'0.85rem',color:IOS.label,mb:0.2}}>Distribución por forma de pago</Typography>
                  <Typography sx={{fontSize:'0.7rem',color:IOS.label2,mb:2}}>Montos por tipo de pago</Typography>
                  <Box sx={{height:{xs:180,sm:200}}}>
                    {formaPago.length===0
                      ?<Box sx={{height:'100%',display:'flex',alignItems:'center',justifyContent:'center'}}><Typography sx={{color:IOS.label2,fontSize:'0.78rem'}}>Sin datos</Typography></Box>
                      :<Doughnut data={chartFP} options={{...baseOpts,cutout:'60%',plugins:{legend:{display:true,position:'bottom',labels:{font:{size:10},color:IOS.label,boxWidth:10,padding:8}},tooltip:{backgroundColor:'rgba(255,255,255,0.97)',titleColor:IOS.label,bodyColor:IOS.label,borderColor:IOS.separator,borderWidth:1,padding:10,cornerRadius:8,callbacks:{label:ctx=>`S/ ${fmtN(ctx.parsed)}`}},datalabels:{display:false}}}}/>
                    }
                  </Box>
                </Box>
                <Box sx={{bgcolor:IOS.surface,borderRadius:'16px',p:2.5,border:`1px solid ${IOS.separator}`,boxShadow:'0 1px 4px rgba(0,0,0,0.07)'}}>
                  <Typography sx={{fontWeight:700,fontSize:'0.85rem',color:IOS.label,mb:0.2}}>Ventas activas por tipo CPE</Typography>
                  <Typography sx={{fontSize:'0.7rem',color:IOS.label2,mb:2}}>Solo comprobantes en estado Activo</Typography>
                  <Box sx={{height:{xs:180,sm:200}}}>
                    {cpe.filter(c=>c.estado?.toUpperCase()==='ACTIVO').length===0
                      ?<Box sx={{height:'100%',display:'flex',alignItems:'center',justifyContent:'center'}}><Typography sx={{color:IOS.label2,fontSize:'0.78rem'}}>Sin datos</Typography></Box>
                      :<Bar data={chartCpe} options={{...baseOpts,scales:{x:{grid:{display:false},ticks:{font:{size:10},color:IOS.label2}},y:{grid:{color:'rgba(0,0,0,0.04)'},ticks:{callback:v=>`S/${v}`,font:{size:9},color:IOS.label2}}},plugins:{legend:{display:false},tooltip:{backgroundColor:'rgba(255,255,255,0.97)',titleColor:IOS.label,bodyColor:IOS.label,borderColor:IOS.separator,borderWidth:1,padding:10,cornerRadius:8,callbacks:{label:ctx=>`S/ ${fmtN(ctx.parsed.y)}`}},datalabels:{color:'white',font:{size:11,weight:'bold'},anchor:'center',align:'center'}}}}/>
                    }
                  </Box>
                </Box>
              </Box>
            </>
          )}

          {!cargando&&!kpi&&aperturaSel&&(
            <Box sx={{py:10,textAlign:'center'}}>
              <Typography sx={{color:IOS.label2,fontSize:'0.85rem'}}>Sin datos para esta apertura</Typography>
            </Box>
          )}

          {!cargando&&!aperturaSel&&aperturas.length===0&&!cargAperturas&&(
            <Box sx={{py:10,textAlign:'center'}}>
              <Box sx={{width:56,height:56,borderRadius:'50%',bgcolor:`${IOS.orange}12`,display:'flex',alignItems:'center',justifyContent:'center',mx:'auto',mb:2}}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={IOS.orange} strokeWidth="1.8" strokeLinecap="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
              </Box>
              <Typography sx={{fontWeight:700,color:IOS.label,mb:0.5}}>Sin aperturas activas</Typography>
              <Typography sx={{color:IOS.label2,fontSize:'0.82rem'}}>No hay sesiones de caja abiertas en este momento</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Modal visor PDF */}
      {modalPrint&&(
        <Box sx={{
          position:'fixed',inset:0,zIndex:9999,
          bgcolor:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center',
          p:{xs:0,sm:2}, backdropFilter:'blur(4px)'
        }} onClick={()=>{ setModalPrint(false); setPdfUrl(null); }}>
          <Box onClick={e=>e.stopPropagation()} sx={{
            bgcolor:IOS.surface,
            borderRadius:{xs:'20px 20px 0 0',sm:'20px'},
            overflow:'hidden', width:{xs:'100%',sm:'92vw'}, maxWidth:{sm:1100},
            height:{xs:'95vh',sm:'92vh'}, display:'flex', flexDirection:'column',
            boxShadow:'0 24px 60px rgba(0,0,0,0.4)',
            position:{xs:'absolute',sm:'relative'}, bottom:{xs:0,sm:'auto'}
          }}>
            <Box sx={{px:2.5,pt:2,pb:1.5,borderBottom:`1px solid ${IOS.separator}`,flexShrink:0,display:'flex',alignItems:'center',justifyContent:'space-between',gap:1}}>
              <Box sx={{minWidth:0,flex:1}}>
                <Typography sx={{fontWeight:700,fontSize:'0.9rem',color:IOS.label,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                  Corte de Caja — {aperturaSel?.descripcion}
                </Typography>
                <Typography sx={{fontSize:'0.72rem',color:IOS.label2}}>Reporte de cierre de sesión</Typography>
              </Box>
              <Box sx={{display:'flex',gap:1,alignItems:'center',flexShrink:0}}>
                {pdfUrl&&['A4','TICKET'].map(f=>(
                  <Box key={f} component="button"
                    onClick={()=>abrirPdf(aperturaSel?.id, f)}
                    sx={{height:30,px:1.4,borderRadius:'8px',border:'none',cursor:'pointer',
                      bgcolor:formatoActivo===f?`${IOS.blue}15`:'transparent',
                      outline:formatoActivo===f?`1.5px solid ${IOS.blue}`:`1px solid ${IOS.separator}`,
                      display:'flex',alignItems:'center'}}>
                    <Typography sx={{fontSize:'0.72rem',fontWeight:700,color:formatoActivo===f?IOS.blue:IOS.label2}}>{f}</Typography>
                  </Box>
                ))}
                {pdfUrl&&(
                  <Box component="a" href={pdfUrl} download={`CorteFarmacia_${aperturaSel?.id}_${formatoActivo}.pdf`}
                    sx={{height:32,px:1.5,borderRadius:'10px', border:`1px solid ${IOS.green}30`, bgcolor:`${IOS.green}08`,
                      cursor:'pointer',color:IOS.green, fontWeight:700, fontSize:'0.78rem', textDecoration:'none',
                      display:'flex',alignItems:'center',gap:0.5, '&:hover':{bgcolor:`${IOS.green}14`}}}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={IOS.green} strokeWidth="2" strokeLinecap="round"><path d="M12 3v13M7 11l5 5 5-5"/><path d="M4 20h16"/></svg>
                    <Box component="span" sx={{display:{xs:'none',sm:'inline'}}}>Descargar</Box>
                  </Box>
                )}
                <Box component="button" onClick={()=>{ setModalPrint(false); setPdfUrl(null); }}
                  sx={{width:30,height:30,borderRadius:'50%',border:'none', bgcolor:'#E5E5EA',cursor:'pointer',
                    display:'flex',alignItems:'center',justifyContent:'center', '&:hover':{bgcolor:'#D1D1D6'}}}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </Box>
              </Box>
            </Box>
            <Box sx={{flex:1,overflow:'hidden',bgcolor:'#525659'}}>
              {cargDet
                ? <Box sx={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',gap:2}}>
                    <CircularProgress sx={{color:'white'}}/>
                    <Typography sx={{color:'rgba(255,255,255,0.8)',fontSize:'0.85rem'}}>Generando reporte...</Typography>
                  </Box>
                : pdfUrl
                  ? <iframe src={pdfUrl} style={{width:'100%',height:'100%',border:'none'}} title="Corte de Caja Farmacia PDF" />
                  : <Box sx={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%'}}>
                      <Typography sx={{color:'rgba(255,255,255,0.6)'}}>No se pudo generar el reporte</Typography>
                    </Box>
              }
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default CorteCajaDatafarma;